import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CgProfile } from "react-icons/cg";
import {
  FiBarChart2,
  FiBox,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiHome,
  FiLogIn,
  FiMenu,
  FiShoppingBag,
  FiShoppingCart,
  FiTag,
  FiUsers,
} from "react-icons/fi";
import { RiSettings3Line, RiStore2Line } from "react-icons/ri";
import { tokenExists, handleLogout, getMe } from "../api/auth";
import InlineLoading from "./InlineLoading";
import { getBranches } from '../api';
import { hasAnyPermission, hasPermission } from "../utils/permissions";
import { NAV_SECTIONS } from "../config/navConfig";
import { normalizePaginatedResponse } from "../utils/pagination";
import {
  requestOpenPosNavbarCart,
  subscribePosNavbarCartState,
} from "../utils/posNavbarCart";

const SECTION_ICONS = {
  home: FiHome,
  pos: FiShoppingBag,
  sales: FiFileText,
  clients: FiUsers,
  catalog: FiTag,
  purchases: FiBox,
  reports: FiBarChart2,
};

const SidebarButton = ({
  icon: Icon,
  label,
  expanded,
  active = false,
  danger = false,
  onClick,
  children,
  ariaExpanded,
}) => (
  <button
    type="button"
    onClick={onClick}
    title={!expanded ? label : undefined}
    aria-label={label}
    aria-expanded={ariaExpanded}
    className={`relative flex h-11 w-full items-center rounded-md text-sm transition ${
      expanded ? "justify-start gap-3 px-3" : "justify-center px-0"
    } ${
      active
        ? "bg-blue-500/20 text-blue-100"
        : danger
          ? "text-red-300 hover:bg-red-500/10 hover:text-red-200"
          : "text-gray-200 hover:bg-gray-800 hover:text-white"
    }`}
  >
    <Icon size={20} className="flex-shrink-0" />
    {expanded && <span className="min-w-0 flex-1 truncate text-left">{label}</span>}
    {expanded && children}
  </button>
);

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isClientPortal = location.pathname.startsWith("/client-portal");
  const match = location.pathname.match(/\/branches\/(\d+)/);
  const branchId = match ? parseInt(match[1]) : localStorage.getItem("selectedBranchId");

  const [user, setUser] = useState(null);
  const [isPinnedOpen, setIsPinnedOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  const [isMediumScreen, setIsMediumScreen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 768px)").matches;
  });
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [branchMenuOpen, setBranchMenuOpen] = useState(false);
  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [branchesRequested, setBranchesRequested] = useState(false);
  const [branchError, setBranchError] = useState("");
  const [openSectionId, setOpenSectionId] = useState(null);
  const [posCartState, setPosCartState] = useState({ visible: false, totalAmount: 0 });

  const isAuthenticated = tokenExists();
  const isExpanded = isMobileSidebarOpen || isPinnedOpen || (isLargeScreen && isHovering);
  const sidebarWidth = isExpanded ? "18rem" : isLargeScreen ? "4rem" : "3.5rem";
  const shouldShowDesktopSidebar = isMediumScreen;
  const shouldShowMobileNavbar = !isMediumScreen;
  const shouldRenderSidebar = shouldShowDesktopSidebar || isMobileSidebarOpen;

  const closeMenus = useCallback(() => {
    setOpenSectionId(null);
    setBranchMenuOpen(false);
    setProfileMenuOpen(false);
  }, []);

  const handleTogglePinned = () => {
    if (!isMediumScreen || isMobileSidebarOpen) {
      setIsMobileSidebarOpen(false);
      closeMenus();
      return;
    }
    setIsPinnedOpen((prev) => {
      const next = !prev;
      if (!next) {
        closeMenus();
      }
      return next;
    });
  };

  const ensureExpanded = () => {
    if (!isExpanded) {
      if (isMediumScreen) {
        setIsPinnedOpen(true);
      } else {
        setIsMobileSidebarOpen(true);
      }
    }
  };

  const toggleSection = (sectionId) => {
    ensureExpanded();
    setProfileMenuOpen(false);
    setBranchMenuOpen(false);
    setOpenSectionId((prev) => (prev === sectionId ? null : sectionId));
  };

  const toggleProfileMenu = () => {
    ensureExpanded();
    setOpenSectionId(null);
    setBranchMenuOpen(false);
    setProfileMenuOpen((prev) => !prev);
  };

  const toggleBranchMenu = () => {
    ensureExpanded();
    setOpenSectionId(null);
    setProfileMenuOpen(false);
    setBranchMenuOpen((prev) => !prev);
  };

  useEffect(() => {
    const fetchUser = async () => {
      if (!tokenExists()) return;
      try {
        const data = await getMe();
        setUser(data);
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mediumQuery = window.matchMedia("(min-width: 768px)");
    const largeQuery = window.matchMedia("(min-width: 1024px)");
    const update = () => {
      const nextIsMedium = mediumQuery.matches;
      const nextIsLarge = largeQuery.matches;
      setIsMediumScreen(nextIsMedium);
      setIsLargeScreen(nextIsLarge);
      if (nextIsMedium) {
        setIsMobileSidebarOpen(false);
      } else {
        setIsPinnedOpen(false);
      }
      if (!nextIsLarge) {
        setIsHovering(false);
      }
    };
    update();
    if (mediumQuery.addEventListener && largeQuery.addEventListener) {
      mediumQuery.addEventListener("change", update);
      largeQuery.addEventListener("change", update);
      return () => {
        mediumQuery.removeEventListener("change", update);
        largeQuery.removeEventListener("change", update);
      };
    }
    mediumQuery.addListener(update);
    largeQuery.addListener(update);
    return () => {
      mediumQuery.removeListener(update);
      largeQuery.removeListener(update);
    };
  }, []);

  const isPosPage = useMemo(
    () =>
      /^\/branches\/\d+\/(purchases\/new|purchases\/\d+\/edit|quotations\/new|quotations\/\d+\/edit)(\/|$)/.test(
        location.pathname
      ),
    [location.pathname]
  );
  const isPosClientView = useMemo(() => {
    if (!isPosPage) return false;
    const params = new URLSearchParams(location.search);
    return String(params.get("posView") || "").toLowerCase() === "client";
  }, [isPosPage, location.search]);

  useEffect(() => {
    const shouldApplySidebar = shouldShowDesktopSidebar && !isClientPortal && !isPosClientView;
    const shouldApplyMobileNavbar = shouldShowMobileNavbar && !isClientPortal && !isPosClientView;
    document.body.classList.toggle("has-app-sidebar", shouldApplySidebar);
    document.body.classList.toggle("has-mobile-navbar", shouldApplyMobileNavbar);
    return () => {
      document.body.classList.remove("has-app-sidebar");
      document.body.classList.remove("has-mobile-navbar");
    };
  }, [shouldShowDesktopSidebar, shouldShowMobileNavbar, isClientPortal, isPosClientView]);

  const navSections = useMemo(() => {
    if (!isAuthenticated) return [];
    const sorted = [...NAV_SECTIONS].sort((a, b) => (a.order || 0) - (b.order || 0));
    return sorted
      .map((section) => {
        const items = (section.items || [])
          .map((item) => {
            const resolvedPath =
              typeof item.path === "function" ? item.path({ branchId }) : item.path;
            if (item.requiresBranch && !branchId) return null;
            if (!resolvedPath) return null;
            if (item.permissions && item.permissions.length) {
              const allowed = item.requireAll
                ? item.permissions.every((permission) => hasPermission(user, permission))
                : hasAnyPermission(user, item.permissions);
              if (!allowed) return null;
            }
            return { ...item, path: resolvedPath };
          })
          .filter(Boolean);
        if (!items.length) return null;
        const isSingle = items.length === 1;
        return {
          ...section,
          items,
          isSingle,
          directPath: isSingle ? items[0].path : null,
        };
      })
      .filter(Boolean);
  }, [user, branchId, isAuthenticated]);

  const publicRoutes = [{ path: "/", label: "Login", icon: FiLogIn }];
  const canReadBranches = hasPermission(user, "branch.read");
  const canAccessSettings = isAuthenticated;
  const shouldShowPosCart = isAuthenticated && isPosPage && !isPosClientView && posCartState.visible;
  const formattedPosCartTotal = Number(posCartState.totalAmount || 0).toLocaleString("de-DE");
  const userDisplayName = String(user?.name || "").trim();
  const userDisplayEmail = String(user?.email || user?.username || "").trim();
  const currentBranchId = branchId ? String(branchId) : "";
  const currentBranch = useMemo(
    () => branches.find((branch) => String(branch.id) === currentBranchId),
    [branches, currentBranchId]
  );
  const currentBranchLabel = currentBranch?.branchName
    || (currentBranchId ? `Sucursal ${currentBranchId}` : "Sin sucursal seleccionada");
  const canChangeBranch = canReadBranches && (!branches.length || branches.length > 1 || !currentBranchId);
  const hasSingleBranch = branches.length === 1;

  const fetchBranches = useCallback(async () => {
    if (!tokenExists() || !canReadBranches) return;
    setBranchesRequested(true);
    setBranchLoading(true);
    setBranchError("");
    try {
      const data = await getBranches({ pageSize: 0 });
      const normalized = normalizePaginatedResponse(data);
      setBranches(normalized.data || []);
    } catch (err) {
      console.error("Error fetching branches:", err);
      setBranchError("No se pudieron cargar las sucursales.");
    } finally {
      setBranchLoading(false);
    }
  }, [canReadBranches]);

  useEffect(() => {
    closeMenus();
    setIsMobileSidebarOpen(false);
  }, [location.pathname, closeMenus]);

  useEffect(() => {
    if (!isAuthenticated || !canReadBranches) return;
    if (branchesRequested || branches.length || branchLoading) return;
    fetchBranches();
  }, [isAuthenticated, canReadBranches, branchesRequested, branches.length, branchLoading, fetchBranches]);

  useEffect(() => {
    const unsubscribe = subscribePosNavbarCartState((event) => {
      const detail = event?.detail || {};
      const nextTotal = Number(detail.totalAmount);
      setPosCartState({
        visible: Boolean(detail.visible),
        totalAmount: Number.isFinite(nextTotal) ? nextTotal : 0,
      });
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!isPosPage) {
      setPosCartState({ visible: false, totalAmount: 0 });
    }
  }, [isPosPage]);

  const routeIsActive = (path) => {
    if (!path) return false;
    return location.pathname === path || (path !== "/" && location.pathname.startsWith(`${path}/`));
  };

  const sectionIsActive = (section) => section.items.some((item) => routeIsActive(item.path));

  const handleOpenBranchModal = () => {
    if (!canChangeBranch) return;
    setBranchModalOpen(true);
    closeMenus();
    setIsMobileSidebarOpen(false);
    if (!branches.length && !branchLoading) {
      fetchBranches();
    }
  };

  const handleSelectBranch = (nextBranchId) => {
    const nextId = String(nextBranchId);
    if (!nextId) return;
    localStorage.setItem("selectedBranchId", nextId);
    setBranchModalOpen(false);
    setBranchMenuOpen(false);
    if (match) {
      const nextPath = location.pathname.replace(/\/branches\/\d+/, `/branches/${nextId}`);
      navigate(`${nextPath}${location.search}`, { replace: true });
    }
  };

  const handleNavigate = (path) => {
    if (!path) return;
    closeMenus();
    setIsMobileSidebarOpen(false);
    if (location.pathname === path) {
      window.location.href = path;
      return;
    }
    navigate(path);
  };

  const handleOpenSettings = () => {
    closeMenus();
    setIsMobileSidebarOpen(false);
    navigate("/settings");
  };

  const handleOpenPosCart = () => {
    closeMenus();
    setIsMobileSidebarOpen(false);
    requestOpenPosNavbarCart();
  };

  const handleLogoClick = () => {
    const canEnterPos = hasAnyPermission(user, ["pos.sell", "pos.open", "pos.close"]);
    const targetPath = canEnterPos && branchId
      ? `/branches/${branchId}/purchases/new`
      : isAuthenticated ? "/home" : "/";
    if (location.pathname === targetPath) {
      window.location.reload();
      return;
    }
    navigate(targetPath);
  };

  const handleLogoutClick = () => {
    closeMenus();
    setIsMobileSidebarOpen(false);
    handleLogout();
  };

  const renderBranchPanel = () => (
    <div className="mt-2 rounded-lg border border-gray-800 bg-gray-950/60">
      <div className="px-3 py-3 border-b border-gray-800">
        <div className="text-xs uppercase text-gray-400">Sucursal actual</div>
        <div className="text-sm font-medium text-gray-100">{currentBranchLabel}</div>
        {!canReadBranches && (
          <div className="text-xs text-gray-400 mt-2">
            No tienes permisos para ver sucursales.
          </div>
        )}
        {branchError && (
          <div className="text-xs text-red-300 mt-2">{branchError}</div>
        )}
        {branchLoading && (
          <div className="text-xs text-gray-400 mt-2">
            <InlineLoading label="Cargando sucursales" />
          </div>
        )}
        {!branchLoading && !branchError && canReadBranches && branches.length === 0 && (
          <div className="text-xs text-gray-400 mt-2">No hay sucursales disponibles.</div>
        )}
      </div>
      <div className="px-3 py-3">
        <button
          type="button"
          onClick={handleOpenBranchModal}
          disabled={!canChangeBranch}
          className="w-full text-left px-3 py-2 rounded bg-gray-800 text-gray-100 hover:bg-gray-700 disabled:opacity-50"
        >
          {currentBranchId ? "Cambiar sucursal" : "Seleccionar sucursal"}
        </button>
        {hasSingleBranch && currentBranchId && (
          <div className="text-xs text-gray-400 mt-2">
            Solo tienes acceso a una sucursal.
          </div>
        )}
      </div>
    </div>
  );

  const renderProfilePanel = () => (
    <div className="mt-2 rounded-lg border border-gray-800 bg-gray-950/60">
      {(userDisplayName || userDisplayEmail) && (
        <div className="px-3 py-3 border-b border-gray-800">
          <p className="text-xs uppercase text-gray-400">Sesión activa</p>
          <p className="text-sm font-medium text-gray-100 truncate">
            {userDisplayName || userDisplayEmail}
          </p>
          {userDisplayName && userDisplayEmail && userDisplayEmail.toLowerCase() !== userDisplayName.toLowerCase() && (
            <p className="text-xs text-gray-400 truncate">{userDisplayEmail}</p>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={() => handleNavigate("/change-password")}
        className="w-full px-3 py-2 text-left text-sm text-gray-100 hover:bg-gray-800 hover:text-white"
      >
        Mi Perfil
      </button>
      <button
        type="button"
        onClick={handleLogoutClick}
        className="w-full px-3 py-2 text-left text-sm text-red-300 hover:bg-red-500/10 hover:text-red-200"
      >
        Cerrar Sesión
      </button>
    </div>
  );

  const renderSection = (section) => {
    const Icon = SECTION_ICONS[section.id] || FiMenu;
    const active = sectionIsActive(section);
    const sectionOpen = openSectionId === section.id;

    if (section.isSingle) {
      return (
        <SidebarButton
          key={section.id}
          icon={Icon}
          label={section.label}
          expanded={isExpanded}
          active={active}
          onClick={() => handleNavigate(section.directPath)}
        />
      );
    }

    return (
      <div key={section.id}>
        <SidebarButton
          icon={Icon}
          label={section.label}
          expanded={isExpanded}
          active={active}
          onClick={() => toggleSection(section.id)}
          ariaExpanded={sectionOpen}
        >
          <FiChevronDown
            size={16}
            className={`flex-shrink-0 transition-transform ${sectionOpen ? "rotate-180" : ""}`}
          />
        </SidebarButton>
        {isExpanded && sectionOpen && (
          <div className="mt-1 space-y-1 pl-9">
            {section.items.map((item) => {
              const itemActive = routeIsActive(item.path);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleNavigate(item.path)}
                  className={`w-full rounded-md px-3 py-2 text-left text-sm transition ${
                    itemActive
                      ? "bg-blue-500/20 text-blue-100"
                      : "text-gray-300 hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (isClientPortal || isPosClientView) return null;

  return (
    <>
      {shouldShowMobileNavbar && (
        <nav className="fixed left-0 top-0 z-[940] flex h-14 w-full items-center justify-between border-b border-gray-800 bg-gray-900 px-3 text-white shadow-md">
          <button
            type="button"
            onClick={() => setIsMobileSidebarOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-300"
            aria-label="Abrir menú"
            aria-expanded={isMobileSidebarOpen}
          >
            <FiMenu size={22} />
          </button>
          <img
            src="/logo.png"
            alt="Logo"
            className="h-9 cursor-pointer"
            onClick={handleLogoClick}
          />
          {shouldShowPosCart ? (
            <button
              type="button"
              onClick={handleOpenPosCart}
              className="relative inline-flex h-10 w-10 items-center justify-center rounded-md text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-300"
              aria-label={`Abrir carrito, total $${formattedPosCartTotal}`}
              title={`Carrito: $${formattedPosCartTotal}`}
            >
              <FiShoppingCart size={22} />
              <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-blue-500 ring-2 ring-gray-900" />
            </button>
          ) : (
            <div className="h-10 w-10" aria-hidden="true" />
          )}
        </nav>
      )}

      {isMobileSidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-[1390] bg-black/50 md:hidden"
          onClick={() => {
            setIsMobileSidebarOpen(false);
            closeMenus();
          }}
          aria-label="Cerrar menú"
        />
      )}

      {shouldRenderSidebar && (
        <aside
          style={{ width: sidebarWidth, minWidth: sidebarWidth, maxWidth: sidebarWidth }}
          className={`fixed inset-y-0 left-0 flex flex-col overflow-hidden border-r border-gray-800 bg-gray-900 text-gray-100 shadow-2xl transition-[width] duration-200 ease-out ${
            isMobileSidebarOpen ? "z-[1400]" : "z-[1300]"
          }`}
          onMouseEnter={() => {
            if (shouldShowDesktopSidebar && isLargeScreen) setIsHovering(true);
          }}
          onMouseLeave={() => {
            if (shouldShowDesktopSidebar && isLargeScreen) setIsHovering(false);
          }}
        >
        <div className="flex-shrink-0 border-b border-gray-800">
          <div className={`flex h-14 items-center ${isExpanded ? "justify-between px-3" : "justify-center"}`}>
            {isExpanded && (
              <img
                src="/logo.png"
                alt="Logo"
                className="h-9 cursor-pointer"
                onClick={handleLogoClick}
              />
            )}
            <button
              type="button"
              onClick={handleTogglePinned}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-200 hover:bg-gray-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-300"
              aria-label={isPinnedOpen ? "Reducir menú" : "Expandir menú"}
              title={isPinnedOpen ? "Reducir menú" : "Expandir menú"}
            >
              {isExpanded ? <FiChevronLeft size={20} /> : <FiChevronRight size={20} />}
            </button>
          </div>
          {!isExpanded && (
            <button
              type="button"
              onClick={handleLogoClick}
              className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-md hover:bg-gray-800"
              aria-label="Ir al inicio"
              title="Ir al inicio"
            >
              <img src="/logo.png" alt="" className="h-8 w-8 object-contain" />
            </button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-4">
          {shouldShowPosCart && (
            <div className="mb-3">
              <SidebarButton
                icon={FiShoppingCart}
                label="Carrito"
                expanded={isExpanded}
                active={false}
                onClick={handleOpenPosCart}
              >
                <span className="text-xs font-semibold text-blue-200">
                  ${formattedPosCartTotal}
                </span>
              </SidebarButton>
            </div>
          )}

          <nav aria-label="Navegación principal" className="space-y-1">
            {isAuthenticated
              ? navSections.map(renderSection)
              : publicRoutes.map((route) => (
                <SidebarButton
                  key={route.path}
                  icon={route.icon}
                  label={route.label}
                  expanded={isExpanded}
                  active={routeIsActive(route.path)}
                  onClick={() => handleNavigate(route.path)}
                />
              ))}
          </nav>
        </div>

        {isAuthenticated && (
          <div className="flex-shrink-0 space-y-1 border-t border-gray-800 px-2 py-4">
            <SidebarButton
              icon={RiStore2Line}
              label={currentBranchLabel}
              expanded={isExpanded}
              active={branchMenuOpen}
              onClick={toggleBranchMenu}
              ariaExpanded={branchMenuOpen}
            >
              <FiChevronDown
                size={16}
                className={`flex-shrink-0 transition-transform ${branchMenuOpen ? "rotate-180" : ""}`}
              />
            </SidebarButton>
            {isExpanded && branchMenuOpen && renderBranchPanel()}

            {canAccessSettings && (
              <SidebarButton
                icon={RiSettings3Line}
                label="Configuración"
                expanded={isExpanded}
                active={routeIsActive("/settings")}
                onClick={handleOpenSettings}
              />
            )}

            <SidebarButton
              icon={CgProfile}
              label={userDisplayName || userDisplayEmail || "Mi Perfil"}
              expanded={isExpanded}
              active={profileMenuOpen}
              onClick={toggleProfileMenu}
              ariaExpanded={profileMenuOpen}
            >
              <FiChevronDown
                size={16}
                className={`flex-shrink-0 transition-transform ${profileMenuOpen ? "rotate-180" : ""}`}
              />
            </SidebarButton>
            {isExpanded && profileMenuOpen && renderProfilePanel()}
          </div>
        )}
        </aside>
      )}

      {branchModalOpen && (
        <div
          className="fixed inset-0 z-[1500] flex items-center justify-center bg-black bg-opacity-50 px-4"
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold">Seleccionar sucursal</h3>
                <p className="text-xs text-gray-500">Elige la sucursal para trabajar.</p>
              </div>
              <button
                type="button"
                onClick={() => setBranchModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                x
              </button>
            </div>

            {!canReadBranches && (
              <div className="mt-4 text-sm text-gray-600">
                No tienes permisos para ver sucursales.
              </div>
            )}
            {branchError && (
              <div className="mt-4 text-sm text-red-600">{branchError}</div>
            )}
            {branchLoading && (
              <div className="mt-4 text-sm text-gray-500">
                <InlineLoading label="Cargando sucursales" />
              </div>
            )}
            {!branchLoading && !branchError && canReadBranches && branches.length === 0 && (
              <div className="mt-4 text-sm text-gray-500">No hay sucursales disponibles.</div>
            )}

            {!branchLoading && !branchError && canReadBranches && branches.length > 0 && (
              <div className="mt-4 max-h-72 space-y-2 overflow-y-auto">
                {branches.map((branch) => {
                  const isSelected = currentBranchId && String(branch.id) === currentBranchId;
                  return (
                    <button
                      key={branch.id}
                      type="button"
                      onClick={() => handleSelectBranch(branch.id)}
                      disabled={isSelected}
                      className={`w-full rounded border px-3 py-2 text-left ${
                        isSelected
                          ? "border-blue-400 bg-blue-50 text-blue-700"
                          : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                      }`}
                    >
                      <div className="text-sm font-medium">{branch.branchName}</div>
                      <div className="text-xs text-gray-500">
                        Sucursal {branch.id}{isSelected ? " (actual)" : ""}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setBranchModalOpen(false)}
                className="rounded bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
